import React, { useState, useMemo, useCallback } from 'react';
import { Component, ComponentType } from '@/types';
import { SharedAdvancedTableComponent } from '@/components/shared/rendering/SharedAdvancedTableComponent';
import { SharedKanbanBoardComponent } from '@/components/shared/rendering/SharedKanbanBoardComponent';
import { SharedGanttChartComponent } from '@/components/shared/rendering/SharedGanttChartComponent';
import { SharedCalendarComponent } from '@/components/shared/rendering/SharedCalendarComponent';
import { Table2, LayoutGrid, GanttChart as GanttIcon, CalendarDays } from 'lucide-react';
import { pmTheme } from './pmTheme';
import {
  isDataBindingFieldMapping,
  isRecord,
  parseComponentDataBinding,
  type ComponentDataBinding,
  type DataBindingFieldMapping,
} from '@/types/componentDataBinding';
import type { ComponentDataResolverResult } from '@/services/component-data/componentDataResolver';

export interface ProjectHubTab {
  id: string;
  label: string;
  componentType: ComponentType;
  enabled: boolean;
  filterConfig?: Record<string, unknown>;
  sortConfig?: Record<string, unknown>;
}

export interface ProjectHubProps {
  component: Component;
  data: Record<string, unknown>[];
  isViewerMode: boolean;
  inheritedDataResolver?: ComponentDataResolverResult;
}

const TAB_ICONS: Record<string, React.FC<{ size?: number | string }>> = {
  'advanced-table': Table2,
  'kanban-board': LayoutGrid,
  'gantt-chart': GanttIcon,
  'calendar': CalendarDays,
};

const DEFAULT_PROJECT_HUB_TAB: ProjectHubTab = {
  id: 'table',
  label: 'Table',
  componentType: ComponentType.ADVANCED_TABLE,
  enabled: true,
};

const DEFAULT_TABS: ProjectHubTab[] = [
  DEFAULT_PROJECT_HUB_TAB,
  { id: 'board', label: 'Board', componentType: ComponentType.KANBAN_BOARD, enabled: true },
  { id: 'timeline', label: 'Timeline', componentType: ComponentType.GANTT_CHART, enabled: true },
  { id: 'calendar', label: 'Calendar', componentType: ComponentType.CALENDAR, enabled: true },
];

function parseProjectHubComponentType(value: unknown): ComponentType | undefined {
  if (value === ComponentType.ADVANCED_TABLE) {
    return ComponentType.ADVANCED_TABLE;
  }
  if (value === ComponentType.KANBAN_BOARD) {
    return ComponentType.KANBAN_BOARD;
  }
  if (value === ComponentType.GANTT_CHART) {
    return ComponentType.GANTT_CHART;
  }
  if (value === ComponentType.CALENDAR) {
    return ComponentType.CALENDAR;
  }

  return undefined;
}

function parseProjectHubTab(value: unknown): ProjectHubTab | undefined {
  if (!isRecord(value)) {
    return undefined;
  }

  const componentType = parseProjectHubComponentType(value.componentType);
  if (
    componentType === undefined ||
    typeof value.id !== 'string' ||
    typeof value.label !== 'string'
  ) {
    return undefined;
  }

  return {
    id: value.id,
    label: value.label,
    componentType,
    enabled: value.enabled !== false,
    ...(isRecord(value.filterConfig) ? { filterConfig: value.filterConfig } : {}),
    ...(isRecord(value.sortConfig) ? { sortConfig: value.sortConfig } : {}),
  };
}

function parseProjectHubTabs(value: unknown): ProjectHubTab[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const tabs = value.map(parseProjectHubTab);
  return tabs.every((tab): tab is ProjectHubTab => tab !== undefined)
    ? tabs
    : undefined;
}

const DURABLE_RECORD_PROPERTY_KEYS = new Set([
  'data',
  'dataBinding',
  'dataSource',
  'sampleData',
  'events',
  'tasks',
  'cards',
  'projects',
  'clients',
  'records',
  'rows',
  'queryTemplateId',
  'writeTarget',
  'writeback',
]);

function readString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value : undefined;
}

function getRecordConfig(value: unknown): Record<string, unknown> {
  return isRecord(value) ? value : {};
}

function omitDurableRecordProperties(
  config: Record<string, unknown>,
): Record<string, unknown> {
  const sanitized: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(config)) {
    if (DURABLE_RECORD_PROPERTY_KEYS.has(key)) {
      continue;
    }
    sanitized[key] = value;
  }

  return sanitized;
}

function getProjectHubFieldMapping(
  properties: Record<string, unknown>,
  binding: ComponentDataBinding | undefined,
): DataBindingFieldMapping | undefined {
  return isDataBindingFieldMapping(properties.fieldMapping)
    ? properties.fieldMapping
    : binding?.fieldMapping;
}

function createInheritedDataBinding(
  binding: ComponentDataBinding | undefined,
  fieldMapping: DataBindingFieldMapping,
): ComponentDataBinding | undefined {
  if (binding === undefined) {
    return undefined;
  }

  return {
    source: binding.source,
    fieldMapping,
    ...(binding.writeTarget === undefined ? {} : { writeTarget: binding.writeTarget }),
  };
}

export const ProjectHubContainer: React.FC<ProjectHubProps> = ({
  component,
  data,
  isViewerMode,
  inheritedDataResolver,
}) => {
  const { properties = {} } = component;
  const inheritedBinding = useMemo(
    () => parseComponentDataBinding(properties.dataBinding),
    [properties.dataBinding],
  );

  const tabs: ProjectHubTab[] = useMemo(() => {
    const configured = parseProjectHubTabs(properties.tabs);
    return (configured && configured.length > 0 ? configured : DEFAULT_TABS)
      .filter(tab => tab.enabled !== false);
  }, [properties.tabs]);

  const [activeTabId, setActiveTabId] = useState<string>(
    () => readString(properties.activeTab) ?? tabs[0]?.id ?? DEFAULT_PROJECT_HUB_TAB.id
  );

  const activeTab = useMemo(
    () => tabs.find(t => t.id === activeTabId) ?? tabs[0] ?? DEFAULT_PROJECT_HUB_TAB,
    [tabs, activeTabId]
  );

  const handleTabChange = useCallback((tabId: string) => {
    setActiveTabId(tabId);
  }, []);

  // Build a child component object with the shared data and tab-specific config
  const childComponent = useMemo((): Component => {
    const fieldMapping = getProjectHubFieldMapping(properties, inheritedBinding);
    const tabConfig = getTabConfig(activeTab, properties, inheritedBinding !== undefined);

    // Map the generic fieldMapping to component-specific field names
    let resolvedFieldMapping: DataBindingFieldMapping | undefined = fieldMapping;
    if (activeTab.componentType === 'kanban-board') {
      // KanbanBoard groups by fieldMapping.column
      const boardConfig = getRecordConfig(properties.boardConfig);
      const baseFieldMapping = fieldMapping ?? {};
      resolvedFieldMapping = {
        ...baseFieldMapping,
        column: readString(boardConfig.columnField)
          ?? baseFieldMapping.status
          ?? 'status',
      };
    } else if (activeTab.componentType === 'gantt-chart') {
      // GanttChart uses taskName, startDate, endDate, assignee
      const baseFieldMapping = fieldMapping ?? {};
      resolvedFieldMapping = {
        ...baseFieldMapping,
        taskName: baseFieldMapping.title ?? 'title',
        startDate: baseFieldMapping.startDate ?? 'startDate',
        endDate: baseFieldMapping.endDate ?? baseFieldMapping.dueDate ?? 'endDate',
        assignee: baseFieldMapping.assignee ?? 'assignee',
      };
    }
    const inheritedChildBinding = inheritedBinding === undefined
      ? undefined
      : createInheritedDataBinding(
          inheritedBinding,
          resolvedFieldMapping ?? inheritedBinding.fieldMapping,
        );

    const inheritedProperties =
      inheritedChildBinding === undefined
        ? {
            data,
            sampleData: data,
          }
        : {
            dataBinding: inheritedChildBinding,
          };

    return {
      ...component,
      id: `${component.id}-${activeTab.id}`,
      type: activeTab.componentType,
      properties: {
        ...tabConfig,
        ...inheritedProperties,
        ...(resolvedFieldMapping === undefined
          ? {}
          : { fieldMapping: resolvedFieldMapping }),
      },
    };
  }, [component, activeTab, data, inheritedBinding, properties]);

  return (
    <div
      data-testid="project-hub-container"
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        color: pmTheme.text,
      }}
    >
      {/* Tab bar */}
      <div
        data-testid="project-hub-tabs"
        style={{
          display: 'flex',
          gap: 2,
          borderBottom: `1px solid ${pmTheme.border}`,
          padding: '0 8px',
          flexShrink: 0,
          backgroundColor: pmTheme.surfaceSubtle,
        }}
      >
        {tabs.map(tab => {
          const Icon = TAB_ICONS[tab.componentType];
          const isActive = tab.id === activeTab.id;
          return (
            <button
              key={tab.id}
              data-testid={`project-hub-tab-${tab.id}`}
              onClick={() => handleTabChange(tab.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 14px',
                fontSize: 13,
                fontWeight: isActive ? 600 : 400,
                color: isActive ? pmTheme.text : pmTheme.textMuted,
                backgroundColor: 'transparent',
                border: 'none',
                borderBottom: isActive ? '2px solid #3b82f6' : '2px solid transparent',
                cursor: 'pointer',
                transition: 'all 150ms',
                marginBottom: -1,
              }}
            >
              {Icon && <Icon size={15} />}
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Active tab content */}
      <div
        data-testid="project-hub-content"
        style={{ flex: 1, overflow: 'auto', minHeight: 0 }}
      >
        {renderTabContent(
          activeTab,
          childComponent,
          isViewerMode,
          inheritedBinding === undefined ? undefined : inheritedDataResolver,
        )}
      </div>
    </div>
  );
};

function getTabConfig(
  tab: ProjectHubTab,
  properties: Record<string, unknown>,
  hasInheritedBinding: boolean,
): Record<string, unknown> {
  const finalizeConfig = hasInheritedBinding
    ? omitDurableRecordProperties
    : (config: Record<string, unknown>) => config;

  switch (tab.componentType) {
    case 'advanced-table':
      return finalizeConfig(getRecordConfig(properties.tableConfig));
    case 'kanban-board': {
      const boardConfig = getRecordConfig(properties.boardConfig);
      // Normalize column definitions: boardConfig uses "title" but KanbanColumn expects "label"
      const rawColumns = boardConfig.columns;
      const normalizedColumns = Array.isArray(rawColumns) && rawColumns.every(isRecord)
        ? rawColumns.map(col => ({
            ...col,
            label: col.label ?? col.title,
          }))
        : undefined;
      return finalizeConfig({
        ...boardConfig,
        columns: normalizedColumns ?? boardConfig.columns,
        dragEnabled: true,
        cardLayout: 'standard',
      });
    }
    case 'gantt-chart':
      return finalizeConfig(getRecordConfig(properties.timelineConfig));
    case 'calendar':
      return finalizeConfig(getRecordConfig(properties.calendarConfig));
    default:
      return {};
  }
}

function renderTabContent(
  tab: ProjectHubTab,
  childComponent: Component,
  isViewerMode: boolean,
  inheritedDataResolver: ComponentDataResolverResult | undefined,
): React.ReactNode {
  switch (tab.componentType) {
    case 'advanced-table':
      return (
        <SharedAdvancedTableComponent
          component={childComponent}
          isViewerMode={isViewerMode}
          inheritedDataResolver={inheritedDataResolver}
        />
      );
    case 'kanban-board':
      return (
        <SharedKanbanBoardComponent
          component={childComponent}
          isViewerMode={isViewerMode}
          inheritedDataResolver={inheritedDataResolver}
        />
      );
    case 'gantt-chart':
      return (
        <SharedGanttChartComponent
          component={childComponent}
          isViewerMode={isViewerMode}
          inheritedDataResolver={inheritedDataResolver}
        />
      );
    case 'calendar':
      return (
        <SharedCalendarComponent
          component={childComponent}
          isViewerMode={isViewerMode}
          inheritedDataResolver={inheritedDataResolver}
        />
      );
    default:
      return (
        <div style={{ padding: 16, color: pmTheme.textMuted, fontSize: 13 }}>
          Unknown view type: {tab.componentType}
        </div>
      );
  }
}

ProjectHubContainer.displayName = 'ProjectHubContainer';
